import {
  collection,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  increment,
  serverTimestamp,
  query,
  where,
  getDocs,
  orderBy,
  limit,
  startAfter,
  DocumentSnapshot
} from 'firebase/firestore';
import { db } from '../lib/firebase';

export interface ViewEvent {
  id: string;
  contentId: string;
  contentType: 'movie' | 'series' | 'episode' | 'short' | 'book' | 'comic' | 'novel';
  userId: string;
  viewedAt: any;
  watchDuration: number;
  completionRate: number;
  deviceInfo: {
    userAgent: string;
    platform: string;
  };
  sessionId: string;
}

export interface ContentViewStats {
  contentId: string;
  contentType: string;
  totalViews: number;
  uniqueViews: number;
  averageWatchTime: number;
  completionRate: number;
  lastViewedAt: any;
  createdAt: any;
  updatedAt: any;
}

export interface UserViewStats {
  userId: string;
  totalWatchTime: number;
  contentWatched: number;
  favoriteContentType: string;
  averageSessionLength: number;
  lastActiveAt: any;
}

export class ViewTrackingService {
  private static readonly COLLECTIONS = {
    VIEW_EVENTS: 'viewEvents',
    CONTENT_STATS: 'contentViewStats',
    USER_STATS: 'userViewStats',
    DAILY_STATS: 'dailyViewStats'
  };

  /**
   * Record a view event with comprehensive tracking
   */
  static async recordView(
    contentId: string,
    contentType: 'movie' | 'series' | 'episode' | 'short' | 'book' | 'comic' | 'novel',
    userId: string,
    watchDuration: number = 0,
    contentDuration: number = 0
  ): Promise<boolean> {
    try {
      const sessionId = this.generateSessionId();
      const completionRate = contentDuration > 0 ? (watchDuration / contentDuration) * 100 : 0;
      
      // Create view event
      const viewEventId = `${contentId}_${userId}_${Date.now()}`;
      const viewEventRef = doc(db, this.COLLECTIONS.VIEW_EVENTS, viewEventId);
      
      const viewEvent: ViewEvent = {
        id: viewEventId,
        contentId,
        contentType,
        userId,
        viewedAt: serverTimestamp(),
        watchDuration,
        completionRate: Math.min(completionRate, 100),
        deviceInfo: {
          userAgent: navigator.userAgent,
          platform: navigator.platform
        },
        sessionId
      };

      await setDoc(viewEventRef, viewEvent);

      // Update content stats
      await this.updateContentStats(contentId, contentType, userId, watchDuration, completionRate);

      // Update user stats
      await this.updateUserStats(userId, contentType, watchDuration);

      // Update daily stats
      await this.updateDailyStats(contentId, contentType);

      return true;
    } catch (error) {
      console.error('Error recording view:', error);
      return false;
    }
  }

  /**
   * Get comprehensive view statistics for content
   */
  static async getContentViewStats(contentId: string): Promise<ContentViewStats | null> {
    try {
      const statsRef = doc(db, this.COLLECTIONS.CONTENT_STATS, contentId);
      const statsSnap = await getDoc(statsRef);
      
      if (statsSnap.exists()) {
        return statsSnap.data() as ContentViewStats;
      }
      
      // Initialize stats if not exists
      const initialStats: ContentViewStats = {
        contentId,
        contentType: 'unknown',
        totalViews: 0,
        uniqueViews: 0,
        averageWatchTime: 0,
        completionRate: 0,
        lastViewedAt: null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      
      await setDoc(statsRef, initialStats);
      return initialStats;
    } catch (error) {
      console.error('Error getting content view stats:', error);
      return null;
    }
  }

  /**
   * Get user's viewing statistics
   */
  static async getUserViewStats(userId: string): Promise<UserViewStats | null> {
    try {
      const statsRef = doc(db, this.COLLECTIONS.USER_STATS, userId);
      const statsSnap = await getDoc(statsRef);
      
      if (statsSnap.exists()) {
        return statsSnap.data() as UserViewStats;
      }
      
      return null;
    } catch (error) {
      console.error('Error getting user view stats:', error);
      return null;
    }
  }

  /**
   * Get recent view events for analytics
   */
  static async getRecentViewEvents(
    limitCount: number = 50,
    lastDoc?: DocumentSnapshot
  ): Promise<ViewEvent[]> {
    try {
      let q = query(
        collection(db, this.COLLECTIONS.VIEW_EVENTS),
        orderBy('viewedAt', 'desc'),
        limit(limitCount)
      );

      if (lastDoc) {
        q = query(q, startAfter(lastDoc));
      }

      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => doc.data() as ViewEvent);
    } catch (error) {
      console.error('Error getting recent view events:', error);
      return [];
    }
  }

  /**
   * Get top viewed content
   */
  static async getTopViewedContent(
    contentType?: string,
    limitCount: number = 10
  ): Promise<ContentViewStats[]> {
    try {
      let q = query(
        collection(db, this.COLLECTIONS.CONTENT_STATS),
        orderBy('totalViews', 'desc'),
        limit(limitCount)
      );

      if (contentType) {
        q = query(q, where('contentType', '==', contentType));
      }

      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => doc.data() as ContentViewStats);
    } catch (error) {
      console.error('Error getting top viewed content:', error);
      return [];
    }
  }

  /**
   * Private helper methods
   */
  private static async updateContentStats(
    contentId: string,
    contentType: string,
    userId: string,
    watchDuration: number,
    completionRate: number
  ): Promise<void> {
    const statsRef = doc(db, this.COLLECTIONS.CONTENT_STATS, contentId);
    const statsSnap = await getDoc(statsRef);

    if (statsSnap.exists()) {
      const currentStats = statsSnap.data() as ContentViewStats;
      const newTotalViews = currentStats.totalViews + 1;
      const newAverageWatchTime = ((currentStats.averageWatchTime * currentStats.totalViews) + watchDuration) / newTotalViews;
      const newCompletionRate = ((currentStats.completionRate * currentStats.totalViews) + completionRate) / newTotalViews;

      await updateDoc(statsRef, {
        totalViews: increment(1),
        averageWatchTime: newAverageWatchTime,
        completionRate: newCompletionRate,
        lastViewedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
    } else {
      await setDoc(statsRef, {
        contentId,
        contentType,
        totalViews: 1,
        uniqueViews: 1,
        averageWatchTime: watchDuration,
        completionRate,
        lastViewedAt: serverTimestamp(),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
    }
  }

  private static async updateUserStats(
    userId: string,
    contentType: string,
    watchDuration: number
  ): Promise<void> {
    const statsRef = doc(db, this.COLLECTIONS.USER_STATS, userId);
    const statsSnap = await getDoc(statsRef);

    if (statsSnap.exists()) {
      await updateDoc(statsRef, {
        totalWatchTime: increment(watchDuration),
        lastActiveAt: serverTimestamp()
      });
    } else {
      await setDoc(statsRef, {
        userId,
        totalWatchTime: watchDuration,
        contentWatched: 1,
        favoriteContentType: contentType,
        averageSessionLength: watchDuration,
        lastActiveAt: serverTimestamp(),
        createdAt: serverTimestamp()
      });
    }
  }

  private static async updateDailyStats(
    contentId: string,
    contentType: string
  ): Promise<void> {
    const today = new Date().toISOString().split('T')[0];
    const dailyStatsRef = doc(db, this.COLLECTIONS.DAILY_STATS, `${contentId}_${today}`);
    
    await updateDoc(dailyStatsRef, {
      contentId,
      contentType,
      date: today,
      views: increment(1),
      updatedAt: serverTimestamp()
    }).catch(async () => {
      // Create if doesn't exist
      await setDoc(dailyStatsRef, {
        contentId,
        contentType,
        date: today,
        views: 1,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
    });
  }

  private static generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }
}
